import type { Location } from '@/models/locationModel';

/**
 * Country names are compared across sources that don't agree on punctuation, accents, or case —
 * "Côte d'Ivoire" vs "Cote d Ivoire", "St. Lucia" vs "St Lucia" — so strip everything but letters.
 */
export function normalizeCountryName(value?: string | null): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z]/gi, '')
    .toLowerCase();
}

/**
 * Every name a location's country could go by.
 *
 * Recommendation locations arrive as a thin projection — often just `google_places_data` — so the
 * model's own `country` leads, backed by both forms of Google's country component ("Croatia" and
 * "HR", since callers can't assume which one their country list uses) and the formatted address
 * tail for records with no address components at all.
 */
export function countryNamesOfLocation(location: Partial<Location>): string[] {
  const names: (string | undefined)[] = [location.country];

  const googleData = location.google_places_data;

  const countryComponent = googleData?.addressComponents?.find((component) =>
    component?.types?.includes?.('country')
  );
  names.push(countryComponent?.longText, countryComponent?.shortText);

  const legacyComponent = googleData?.address_components?.find((component) =>
    component?.types?.includes?.('country')
  );
  names.push(legacyComponent?.long_name, legacyComponent?.short_name);

  const address = location.a3_intl_address || googleData?.formattedAddress || '';
  names.push(address.split(',').pop()?.trim());

  return names.filter((name): name is string => !!name);
}

/** True when any of the location's country names matches `country`. */
export function locationIsInCountry(
  location: Partial<Location>,
  country?: string | null
): boolean {
  const target = normalizeCountryName(country);
  if (!target) return false;
  return countryNamesOfLocation(location).some(
    (name) => normalizeCountryName(name) === target
  );
}

/**
 * The locations in `country`, or all of them when there's no country to limit to — e.g. the
 * add-place searches of a list scoped to a whole country. A location whose country can't be
 * told is left out rather than guessed.
 */
export function onlyLocationsInCountry<T extends object>(
  locations: T[],
  country?: string | null
): T[] {
  if (!normalizeCountryName(country)) return locations;
  // Search results use the `@/models/location` class; it carries the same country, address and
  // Google fields this reads, just typed more loosely.
  return locations.filter((location) =>
    locationIsInCountry(location as unknown as Partial<Location>, country)
  );
}
